/* eslint-disable react/prop-types */
import {
  Grid,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  TextField,
  Select,
  FormControl,
  InputLabel,
  MenuItem,
  IconButton,
  Button,
  Autocomplete
} from '@material-ui/core';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PropTypes from 'prop-types';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import AddIcon from '@mui/icons-material/Add';
import React from 'react';
import Popper from '@mui/material/Popper';
import { getSizeLabel } from './LRUSize';
import { diskSizeMap } from './NewVMModal';

export const diskSizes = ['small', 'medium', 'large', 'xlarge'];

const sizes = ['small', 'medium', 'large', 'xlarge', 'custom'];

export function ValidateDiskSize(disk) {
  const re = /^[0-9]+[GM]$/;
  // check if diskSize is valid if provided
  if (disk.diskSize) {
    if (!re.test(disk.diskSize)) {
      return false;
    }

    const value = parseInt(disk.diskSize, 10);
    if (value <= 0) {
      return false;
    }
    // Disk size is too large, larger than 2TB or 2000G
    if (disk.diskSize.includes('G') && value > 2000) {
      return false;
    }
    // Disk size is too large, larger than 2TB or 2000000M
    if (disk.diskSize.includes('M') && value > 2000000) {
      return false;
    }
  }
  return true;
}

// retrieves the available disk images from the list of vm template disks
function getAvailableImageList(vmTemplates) {
  const availableImages = new Set(['']);
  if (vmTemplates && vmTemplates.length > 0) {
    vmTemplates.forEach((vm) => {
      vm.disks?.forEach((disk) => {
        if (disk.image) {
          availableImages.add(disk.image);
        }
      });
    });
  }
  return [...availableImages];
}

// this wide popper allows the ability for the select dropdown for disk images to take up full width
const WidePopper = (props) => {
  const { style, ...other } = props;
  return (
    <Popper
      placement="bottom-start"
      {...other}
      style={{
        ...style,
        width: 'auto', // override MUI's forced width
        minWidth: style?.width, // keep at least the input width
        overflowX: 'auto'
      }}
    />
  );
};

VMDiskSettings.propTypes = {
  currentVM: PropTypes.object,
  updateCurrentVM: PropTypes.func,
  vmTemplates: PropTypes.array
};

// eslint-disable-next-line no-unused-vars
export function VMDiskSettings({ currentVM, updateCurrentVM, vmTemplates }) {
  const availableImageList = getAvailableImageList(vmTemplates);

  const setSize = (index, s) => {
    // if not custom, update the disk size accordingly
    if (s !== 'custom') {
      const newVM = {
        ...currentVM
      };
      newVM.disks[index].size = s;
      setDiskSize(s, index);
      updateCurrentVM(newVM);
    } else {
      const newVM = {
        ...currentVM
      };
      newVM.disks[index].size = s;
      updateCurrentVM(newVM);
    }
  };

  const handleDiskImage = (index, image) => {
    const newVM = { ...currentVM };
    newVM.disks[index].image = image;

    updateCurrentVM(newVM);
  };

  const handleRemoveDisk = (diskIndex) => {
    const newVM = { ...currentVM };
    newVM.disks.splice(diskIndex, 1);

    updateCurrentVM(newVM);
  };

  // add a new disk to the list of disks
  const handleAddDisk = () => {
    const newVM = { ...currentVM };

    // this adds another disk with the default values set already
    newVM.disks.push({
      image: '',
      diskSize: `${diskSizeMap.small}G`,
      size: sizes[0]
    });

    updateCurrentVM(newVM);
  };

  const setDiskSize = (size, index) => {
    const newVM = { ...currentVM };
    newVM.disks[index].diskSize = `${diskSizeMap[size]}G`;
    updateCurrentVM(newVM);
  };

  const handleCustomDiskSizeChange = (index, newSize) => {
    const newVM = { ...currentVM };
    newVM.disks[index].diskSize = `${newSize}`;

    updateCurrentVM(newVM);
  };

  return (
    <Grid item xs={12}>
      <Accordion>
        <AccordionSummary sx={{ paddingLeft: 0 }} expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Disk Settings</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack direction="column" spacing={2} data-testid="vm-disks">
            <Typography value="VM disks" variant="overline">
              VM Disks
            </Typography>
            {currentVM.disks?.map((disk, index) => (
              <Grid container xs={12} columnSpacing={2} key={index}>
                <Grid item xs={7}>
                  <FormControl fullWidth="true">
                    <Autocomplete
                      options={availableImageList}
                      freeSolo
                      autoSelect
                      onChange={(_, newValue) => {
                        handleDiskImage(index, newValue);
                      }}
                      getOptionDisabled={(option) =>
                        index === 0 ? option === availableImageList[0] : null
                      }
                      getOptionLabel={(option) => (option !== '' ? option : 'Blank Disk')}
                      SelectDisplayProps={{ 'data-testid': `set-image-${index}` }}
                      data-testid={`set-image-${index}`}
                      value={disk.image}
                      fullWidth
                      size="small"
                      PopperComponent={WidePopper}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={index === 0 ? 'OS Disk Image Source' : 'Data Disk Image Source'}
                        />
                      )}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="size-label">Size</InputLabel>
                    <Select
                      label="Size"
                      fullWidth
                      size="small"
                      value={disk.size}
                      onChange={(e) => {
                        setSize(index, e.target.value);
                      }}
                      SelectDisplayProps={{ 'data-testid': `set-size-select-${index}` }}
                    >
                      {sizes.map((size, index) => (
                        <MenuItem key={index} value={size}>
                          {getSizeLabel(size)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={2}>
                  <TextField
                    data-testid={`input-disk-size-${index}`}
                    disabled={disk.size !== 'custom'}
                    label="Disk Size"
                    value={disk.diskSize ? disk.diskSize : diskSizeMap[disk.size]}
                    size="small"
                    onChange={(e) => handleCustomDiskSizeChange(index, e.target.value)}
                    error={!ValidateDiskSize(disk)}
                  />
                </Grid>
                <Grid item xs={1}>
                  <IconButton
                    data-testid={`remove-disk-btn-${index}`}
                    color="error"
                    onClick={() => handleRemoveDisk(index)}
                    disabled={index === 0}
                  >
                    <DeleteOutlineOutlinedIcon />
                  </IconButton>
                </Grid>
              </Grid>
            ))}
            <Button
              data-testid="add-disk-btn"
              startIcon={<AddIcon />}
              variant="contained"
              sx={{ maxWidth: 220 }}
              onClick={() => handleAddDisk()}
            >
              Add Disk
            </Button>
          </Stack>
        </AccordionDetails>
      </Accordion>
    </Grid>
  );
}
